import { useState, useRef, useCallback, useEffect } from "react"
import { ImageIcon, XIcon, Crop, Check, X, ZoomIn, ZoomOut } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import Cropper from 'react-easy-crop'
import { applyCropSettings, ratioToCropSettings } from "@/utils/crop-utils"
import { CropSettings } from "@/lib/types"
import { useTranslation } from "react-i18next"

export interface ImageDropZoneProps {
  onImageSelected: (file: File, preview: string, cropSettings: CropSettings) => void
  onImageCleared: () => void
  preview_label_url: string | null
  targetRatio: number
  image: File | null
  isExistingImage?: boolean
  labelCropSettings?: CropSettings
  onCroppingStateChange?: (isCropping: boolean) => void
}

export const ImageDropZone = ({
  onImageSelected,
  onImageCleared,
  preview_label_url,
  targetRatio,
  labelCropSettings,
  onCroppingStateChange,
}: ImageDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isCropping, setIsCropping] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)
  const [originalImage, setOriginalImage] = useState<string | null>(null)
  const [originalImageFile, setOriginalImageFile] = useState<File | null>(null)
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleAreaClick = () => {
    if (!isCropping) {
      fileInputRef.current?.click()
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file?.type.startsWith("image/")) {
      handleFile(file)
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
  }

  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      const imageUrl = reader.result as string
      setOriginalImage(imageUrl)
      setOriginalImageFile(file) // Store the original file

      // Load image to get dimensions and apply crop settings
      const img = new Image()
      img.onload = async () => {
        try {
          const cropSettings = ratioToCropSettings(targetRatio, img.width, img.height)
          const croppedFile = await applyCropSettings(imageUrl, cropSettings)

          const croppedReader = new FileReader()
          croppedReader.onload = () => {
            // Pass original file with cropped preview and crop settings
            onImageSelected(file, croppedReader.result as string, cropSettings)
          }
          croppedReader.readAsDataURL(croppedFile)
        } catch (error) {
          console.error('Error applying crop settings:', error)
          // Fallback to original image if cropping fails
          onImageSelected(file, imageUrl, { x: 0, y: 0, width: img.width, height: img.height })
        }
      }
      img.src = imageUrl
    }
    reader.readAsDataURL(file)
  }

  const clearImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsCropping(false)
    setOriginalImage(null)
    setOriginalImageFile(null)
    setCroppedPreviewUrl(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    onImageCleared()
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const startCropping = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (preview_label_url && !originalImage) {
      setOriginalImage(preview_label_url)

      // Fetch the original image as a file for existing images
      if (!originalImageFile) {
        try {
          const response = await fetch(preview_label_url)
          const blob = await response.blob()
          const file = new File([blob], 'existing-image.jpg', {
            type: blob.type || 'image/jpeg',
            lastModified: Date.now(),
          })
          setOriginalImageFile(file)
        } catch (error) {
          console.warn('Could not fetch existing image as file:', error)
        }
      }
    }

    // Initialize crop settings if they exist
    if (labelCropSettings) {
      setCroppedAreaPixels(labelCropSettings)
    }

    setIsCropping(true)
    onCroppingStateChange?.(true)
  }

  const cancelCropping = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsCropping(false)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    onCroppingStateChange?.(false)
  }

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const applyCrop = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!originalImage || !croppedAreaPixels) return

    try {
      const cropSettings: CropSettings = {
        x: croppedAreaPixels.x,
        y: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height
      }
      console.log('Applying crop settings:', cropSettings)
      const croppedFile = await applyCropSettings(originalImage, cropSettings, true)
      const reader = new FileReader()
      reader.onload = () => {
        const croppedPreview = reader.result as string
        // Update the cropped preview URL
        setCroppedPreviewUrl(croppedPreview)

        // Always pass the ORIGINAL file, not the cropped one
        const fileToPass = originalImageFile || new File([croppedFile], 'image.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })

        // Pass original file, cropped preview, and crop settings
        console.log('cropSettings:', cropSettings)
        onImageSelected(fileToPass, croppedPreview, cropSettings)
        setIsCropping(false)
        onCroppingStateChange?.(false)
      }
      reader.readAsDataURL(croppedFile)
    } catch (error) {
      console.error('Error cropping image:', error)
    }
  }

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    setZoom(Number(e.target.value))
  }

  const adjustZoom = useCallback((delta: number) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setZoom(prev => Math.max(1, Math.min(3, prev + delta)))
  }, [])

  // Apply crop settings to existing image when available
  useEffect(() => {
    const applyCropToExistingImage = async () => {
      if (preview_label_url && labelCropSettings) {
        try {
          const croppedFile = await applyCropSettings(preview_label_url, labelCropSettings, true)
          const reader = new FileReader()
          reader.onload = () => {
            setCroppedPreviewUrl(reader.result as string)
          }
          reader.readAsDataURL(croppedFile)
        } catch (error) {
          console.warn('CORS or crop error with existing image, using original:', error)
          setCroppedPreviewUrl(preview_label_url)
        }
      } else if (preview_label_url && !labelCropSettings) {
        setCroppedPreviewUrl(preview_label_url)
      } else if (!preview_label_url) {
        setCroppedPreviewUrl(null)
      }
    }

    applyCropToExistingImage()
  }, [preview_label_url, labelCropSettings])

  const { t } = useTranslation("common");
  return (
    <div
      className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-colors ${isDragging ? "border-primary bg-primary/10" : "border-muted-foreground/25"
        } ${!isCropping ? "cursor-pointer" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleAreaClick}
      style={{ minHeight: isCropping ? '400px' : '200px' }}
    >
      {isCropping && originalImage ? (
        <div className="absolute inset-0 rounded-lg overflow-hidden">
          <Cropper
            image={originalImage}
            crop={crop}
            zoom={zoom}
            aspect={targetRatio}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            initialCroppedAreaPixels={croppedAreaPixels || undefined}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
                borderRadius: '8px'
              }
            }}
          />

          {/* Action buttons */}
          <div className="absolute top-2 right-2 flex gap-2 z-10">
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-green-500 text-white hover:bg-green-600"
              onClick={applyCrop}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-red-500 text-white hover:bg-red-600"
              onClick={cancelCropping}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Zoom controls */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-black/50 rounded-lg px-3 py-2 z-10">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={adjustZoom(-0.1)}
              disabled={zoom <= 1}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={zoom}
              onChange={handleZoomChange}
              className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white hover:bg-white/20"
              onClick={adjustZoom(0.1)}
              disabled={zoom >= 3}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <span className="text-white text-xs min-w-[3rem] text-center">
              {(zoom * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      ) : preview_label_url ? (
        <div className="relative w-full py-8">
          <div className="flex gap-2 absolute top-2 right-2 z-10">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full bg-blue-500 text-white hover:bg-blue-600"
              onClick={startCropping}
              title="Crop/Resize image"
            >
              <Crop className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="rounded-full bg-red-500 text-white hover:bg-red-600"
              onClick={clearImage}
              title="Remove image"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
          <img
            src={croppedPreviewUrl || preview_label_url}
            alt="Preview"
            className="mx-auto max-h-[300px] object-contain"
          />
          <p className="mt-2 text-center text-sm text-muted-foreground">
            Current image
          </p>
        </div>
      ) : (
        <>
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mb-4">
            <ImageIcon className="h-10 w-10 text-primary" />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-medium">{t("common:dropZone.dragAndDrop")}</p>
            <p className="text-xs text-muted-foreground">{t("common:dropZone.browseFiles")}</p>
          </div>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={handleFileInput}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      )}
    </div>
  )
}
